/**
 * "Ask MAHI" — the idle representation of the conversation surface, and the
 * single dominant control in Discover's bottom row.
 *
 * Replaces the old robot orb (MahiOrb). The orb was mystery meat: on the most
 * important screen in the app, the most visually dominant control was the only
 * one that never said what it did — its purpose lived in an aria-label and in
 * one tour step seen once and forgotten. This states the action instead.
 * MAHI's face is not lost by the change; MahiRobot still carries the character
 * in the chat panel, the thinking indicator, the route loader, the wardrobe
 * empty state and the auth screens.
 *
 * WHERE THE COLOUR COMES FROM
 *
 * The palette below is sampled from the illustrated section marks (see
 * components/ui/marks.jsx — the wardrobe dress, the Almost Yours tote), which
 * are the only place MAHI's own visual language is fully expressed: soft
 * flowing ribbon forms washing from coral through apricot into lavender on a
 * diagonal. Those are measured values off the artwork, not an approximation of
 * it, so this control belongs to the same family as the marks beside it rather
 * than merely coordinating with them. If the marks are ever redrawn, re-sample
 * rather than nudging these by eye.
 *
 * An earlier pass made this a monochrome slate lockup on a white disc inside a
 * mint ring with a single teal halo. It was clean and it was anonymous: the
 * ring and glow were generic UI chrome, and nothing about it said MAHI. Teal
 * is the voice colour for her *messages*; it is not her identity, and using it
 * here put a third accent on a screen that already carries raspberry actions
 * and the coral/lavender marks.
 *
 * FOUR THINGS ARE LOAD-BEARING
 *
 * 1. ASK is set in a typeface, MAHI is the drawn mark, and that split is
 *    deliberate rather than a shortcut. MAHI_GLYPHS only contains M, A, H and
 *    I — there is no S or K to borrow — so ASK could never have been drawn
 *    from the same geometry. Making a virtue of it gives the lockup two
 *    registers: MAHI is brand and dominant (the exact mark, deep warm
 *    charcoal, 70% of the disc), ASK is UI and deliberately recessive (7px,
 *    wide tracking, a warm grey that reads as a label rather than a second
 *    word). The gap between them is the hierarchy — roughly 2.8:1 on cap
 *    height. Because a font's stroke weight will never natively agree with the
 *    mark's hairline stems, ASK's size/weight/tracking are tuned by eye
 *    against the mark, not derived; re-check in a browser if either changes.
 *
 * 2. The wordmark sits above true centre, not on it. The wave occupies the
 *    base of the disc, so a mathematically centred lockup reads as sunk into
 *    it. OPTICAL_LIFT is what makes the composition look placed instead of
 *    defaulted.
 *
 * 3. The two waves are the brand's fluid language, and they are the only thing
 *    in the disc allowed to move at rest — slowly, on a long loop, in the
 *    bottom third, well clear of the letterforms. They are deliberately faint
 *    and shallow: a pass that raised them to 46% of the disc at high opacity
 *    made the control louder but turned the disc into a picture with a
 *    wordmark on top, and the wash started competing with the letters it is
 *    supposed to sit under. Presence belongs in the halo, which is outside the
 *    disc and cannot crowd the type; the wash's job is only to keep the
 *    interior from reading as flat white. MahiLogo's whole premise is
 *    that the four letters are flush top and bottom (see the offset-line
 *    solving in its header), so a permanent per-letter bob was rejected: it
 *    would break the alignment that file exists to guarantee, and this control
 *    sits directly under a card the shopper is actively dragging, where
 *    sustained motion on the type itself competes with the gesture. Motion in
 *    the wash is free; motion on the mark is not.
 *
 * 4. The ripple is an event, not a loop. On a beat (see useAskBeat for when),
 *    a wave runs left to right through the lockup: ASK lifts, then each letter
 *    in turn rises a couple of pixels and settles. It never reorders or
 *    scrambles the letters — with H and I being near-identical bars, a shuffle
 *    reads as a broken render rather than as play, and it would put a mangled
 *    brand mark on screen. Rendering MAHI_GLYPHS by hand rather than using
 *    <MahiLogo/> is the only reason this file touches the raw geometry: the
 *    letters have to move independently, which the static mark deliberately
 *    cannot express.
 *
 * Sizing is constrained by the Undo pill above, not by this control's own
 * needs — see RAISE. That constraint is why presence here is bought with light
 * rather than with size: when the card gained a brand pill, MAHI's own line and
 * vibe chips, this control started reading as the quiet one on a loud screen,
 * and the fix was to raise the halo opacity, thicken the rim to 1.5px and
 * deepen the drop — all of which happen outside the disc. Every one of those is
 * free. Growing the button is not: it would either cross into the Undo row or
 * take height from the card stage, which is `flex-1` and pays for everything.
 */
import React, { useEffect } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { MAHI_GLYPHS, MAHI_VIEWBOX } from '../ui/MahiLogo';
import useReducedMotion from '../../hooks/useReducedMotion';

// Sampled from mahi/brand/wardrobe-icon-v2-256.webp — see the note above.
const CORAL = '#EE9B96';
const APRICOT = '#F2C4B1';
const LAVENDER = '#C7A8D5';
const VIOLET = '#C693C9';

// A warm charcoal carrying a slight violet cast, so the type sits in the same
// temperature as the wash instead of reading as a cool grey dropped onto a
// warm ground. Well past AA on the near-white disc at this weight.
const INK = '#2B2430';
const ASK_INK = '#9A8FA3';

// The button is pulled up out of the action row so it reads as the row's
// anchor rather than as one of three equals. This number is not free: the
// Undo row above is `h-11` (44px) around a ~27px pill, so only ~8px of it is
// actually empty, and the old orb's -mt-8 against the row's pt-3 pushed it
// 20px up into that space — straight through the pill on a phone. Keeping the
// raise equal to the action row's top padding makes the intrusion exactly
// zero, so the two can never collide again whatever the pill's height turns
// out to be. If the row's `pt-3` changes, change this with it.
const RAISE = 12;        // px, must equal the action row's top padding
const OUTER = 72;        // px, full touch target, and the glow's box
const DISC = 66;         // px, outer edge of the hairline gradient rim
const MARK_W = 46;       // px, the MAHI mark at ~70% of the disc
const OPTICAL_LIFT = 3;  // px the type block sits above true centre

// How far a letter rises at the crest of the ripple. ASK is HTML and moves in
// px; the mark is SVG and moves in viewBox units, where one screen pixel is
// MAHI_VIEWBOX's width / MARK_W — so the two lifts are the same ~2.2px on
// screen despite the very different numbers.
const LIFT_PX = 2.2;
const LIFT_UNITS = Math.round((LIFT_PX * 389.706) / MARK_W);

const STAGGER = 0.06;    // s between the ripple reaching one letter and the next
const RIPPLE = 0.5;      // s for a single letter's rise and settle

// One period of the wave is 100 units and the path lays down two of them, so
// translating the strip by exactly -50% is seamless however long it loops.
const WAVE_FRONT = 'M0,15 C12,11 24,11 36,14 C48,17 60,18 72,15 C84,12 94,12 100,15 C112,11 124,11 136,14 C148,17 160,18 172,15 C184,12 194,12 200,15 L200,32 L0,32 Z';
const WAVE_BACK  = 'M0,19 C10,16 22,15 34,18 C46,21 58,22 70,19 C82,16 92,16 100,19 C110,16 122,15 134,18 C146,21 158,22 170,19 C182,16 192,16 200,19 L200,32 L0,32 Z';

export default function MahiAskButton({ layoutId, onOpen, beat = 0 }) {
  const reduced = useReducedMotion();
  const wave = useAnimationControls();

  // A beat is a moment, not a queue: if reduced motion is on we simply never
  // start one, and the lockup is entirely legible standing still.
  useEffect(() => {
    if (!beat || reduced) return;
    // `custom` carries each element's place in the ripple and the lift in its
    // own coordinate system, since ASK and the mark do not share units.
    wave.start(({ step, lift }) => ({
      y: [0, -lift, 0],
      transition: { duration: RIPPLE, delay: step * STAGGER, ease: [0.34, 1.2, 0.64, 1] },
    }));
  }, [beat, reduced, wave]);

  const drift = (duration, from, to) => (reduced
    ? { animate: { x: from }, transition: { duration: 0 } }
    : { animate: { x: [from, to] }, transition: { duration, repeat: Infinity, ease: 'linear' } });

  return (
    <motion.button
      type="button"
      // Reduced motion drops the shared-layout id entirely rather than just
      // speeding the spring up — with no layoutId, framer has nothing to
      // spatially FLIP, so mount/unmount falls back to the plain opacity
      // fade below: a real cross-fade, not a fast morph.
      layoutId={reduced ? undefined : layoutId}
      initial={reduced ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onOpen}
      data-testid="mahi-ask-button"
      // Still "orb" because DiscoveryTour keys its spotlight off this name and
      // useMahiConversation calls this phase 'orb'. Both mean "the idle
      // conversation surface", which is still exactly what this is; renaming
      // them is a separate sweep, not part of the visual change.
      data-tour="orb"
      aria-label="Ask MAHI"
      aria-haspopup="dialog"
      style={{ borderRadius: 9999, width: OUTER, height: OUTER, marginTop: -RAISE }}
      className="relative shrink-0 flex items-center justify-center active:scale-95 transition-transform z-10"
      transition={reduced ? { duration: 0.18, ease: 'easeInOut' } : { type: 'spring', stiffness: 260, damping: 28, mass: 0.9 }}
    >
      {/* The halo is two offset tones rather than one concentric ring. A single
          hue pulsing evenly outward is the default platform glow and reads as
          chrome; coral riding high-left against lavender low-right, each
          breathing on its own clock, reads as light coming off the artwork.
          They are deliberately out of phase so the halo never returns to a
          symmetrical resting state. */}
      <motion.div
        className="absolute rounded-full"
        style={{ inset: -2, background: CORAL, filter: 'blur(11px)', transformOrigin: '38% 32%' }}
        animate={reduced ? { opacity: 0.34 } : { opacity: [0.26, 0.48, 0.26], scale: [0.94, 1.08, 0.94] }}
        transition={reduced ? { duration: 0 } : { duration: 4.6, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute rounded-full"
        style={{ inset: -2, background: LAVENDER, filter: 'blur(11px)', transformOrigin: '64% 72%' }}
        animate={reduced ? { opacity: 0.32 } : { opacity: [0.46, 0.24, 0.46], scale: [1.07, 0.95, 1.07] }}
        transition={reduced ? { duration: 0 } : { duration: 6.1, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* The rim is a 1px gradient, not a flat ring: a solid band at this
          diameter is a second concentric shape competing with the disc, and a
          thick one is what made the earlier pass look like a toy. Painting it
          as a gradient border (two backgrounds, one clipped to the padding box
          and one to the border box) keeps it hairline while still carrying the
          coral-to-lavender sweep the marks use. */}
      <div
        style={{
          width: DISC,
          height: DISC,
          borderRadius: 9999,
          padding: 1.5,
          background: `linear-gradient(145deg, ${CORAL} 0%, ${APRICOT} 42%, ${LAVENDER} 78%, ${VIOLET} 100%)`,
          boxShadow: '0 1px 2px rgba(43,36,48,0.08), 0 14px 34px -12px rgba(198,147,201,0.72)',
        }}
        className="relative"
      >
        <div
          style={{ borderRadius: 9999, background: 'linear-gradient(150deg, #FFFCFA 0%, #FEFAFB 55%, #FBF8FD 100%)' }}
          className="relative w-full h-full overflow-hidden flex flex-col items-center justify-center"
        >
          {/* The wash: two ribbons in the bottom third, the near one warm and
              the far one cool, drifting in opposite directions on long
              mismatched loops so they never resolve into a repeating pattern.
              Kept low and low-contrast — this is the brand speaking quietly
              under the type, never behind it. */}
          <div className="absolute inset-x-0 bottom-0 h-[32%] overflow-hidden pointer-events-none" aria-hidden="true">
            <motion.svg
              viewBox="0 0 200 32" preserveAspectRatio="none"
              className="absolute inset-y-0 left-0 h-full w-[200%]"
              {...drift(26, '0%', '-50%')}
            >
              <defs>
                <linearGradient id="mahiWaveBack" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={LAVENDER} />
                  <stop offset="100%" stopColor={VIOLET} />
                </linearGradient>
              </defs>
              <path d={WAVE_BACK} fill="url(#mahiWaveBack)" opacity="0.22" />
            </motion.svg>
            <motion.svg
              viewBox="0 0 200 32" preserveAspectRatio="none"
              className="absolute inset-y-0 left-0 h-full w-[200%]"
              {...drift(19, '-50%', '0%')}
            >
              <defs>
                <linearGradient id="mahiWaveFront" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={CORAL} />
                  <stop offset="55%" stopColor={APRICOT} />
                  <stop offset="100%" stopColor={LAVENDER} />
                </linearGradient>
              </defs>
              <path d={WAVE_FRONT} fill="url(#mahiWaveFront)" opacity="0.3" />
            </motion.svg>
          </div>

          {/* The lockup, lifted off true centre — see OPTICAL_LIFT. */}
          <div
            style={{ marginTop: -OPTICAL_LIFT }}
            className="relative flex flex-col items-center gap-[5px]"
          >
            <motion.span
              custom={{ step: 0, lift: LIFT_PX }}
              animate={wave}
              aria-hidden="true"
              style={{ color: ASK_INK }}
              className="font-mahi-sans text-[7px] font-semibold leading-none tracking-[0.26em] indent-[0.26em]"
            >
              ASK
            </motion.span>

            {/* One <g> per letter so the ripple can run through them. Sized by
                width; height follows the viewBox. */}
            <svg
              viewBox={MAHI_VIEWBOX}
              style={{ width: MARK_W, color: INK }}
              className="h-auto"
              fill="currentColor"
              aria-hidden="true"
            >
              {MAHI_GLYPHS.map((glyph, i) => (
                <motion.g key={glyph.id} custom={{ step: i + 1, lift: LIFT_UNITS }} animate={wave}>
                  <path transform={glyph.transform} d={glyph.d} />
                </motion.g>
              ))}
            </svg>
          </div>
        </div>
      </div>
    </motion.button>
  );
}
